import Head from "next/head";

// ** API
import articlesApi from "@/api/articles/api";
import tagsApi from "@/api/tags/api";

// ** Components
import CategoryCard from "@/components/CategoryCard";

// ** MUI
import { Container, Grid2 as Grid, Typography } from "@mui/material";

// ** Layout
import DefaultLayout from "../../../../layout/DefaultLayout";

export async function getStaticPaths() {
  // Fetch all technologies to get their slugs
  const tags = await tagsApi.getTags();

  // Map over the technologies to create paths
  const paths = tags.data.map((tag) => ({
    params: { tag: tag.slug },
  }));

  return {
    paths,
    fallback: false, // See explanation below
  };
}

export async function getStaticProps({ params }) {
  const { tag } = params;

  const articlesRes = await articlesApi.getArticlesByTag(tag);

  return {
    props: {
      tagWithArticles: {
        tag,
        articles: articlesRes.data,
      },
    },
    revalidate: 10, // Re-generate the page every 10 seconds
  };
}

function TagArticles({ tagWithArticles }) {
  const { tag, articles } = tagWithArticles;

  const siteUrl = "https://valentinmarko.com";
  const canonicalUrl = `${siteUrl}/blog/tag/${tag}`;
  const hookCorrections = {
    uselayouteffect: "useLayoutEffect",
    useeffect: "useEffect",
    usestate: "useState",
  };

  const formattedTag = tag.replace(/-/g, " ");

  const words = formattedTag.split(" ");

  const firstWordLower = words[0].toLowerCase();

  // Replace first word if it matches a known hook
  if (hookCorrections[firstWordLower]) {
    words[0] = hookCorrections[firstWordLower];
  } else {
    // Otherwise just capitalize normally
    words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);
  }

  const tagLabel = words.join(" ");

  // const tagLabel = tag.charAt(0).toUpperCase() + tag.slice(1);

  const title = `${tagLabel} Articles | Valentin-Marko – Software Development Blog`;

  const description = `Browse articles tagged with ${tagLabel}. Tutorials, tips, and best practices related to ${tagLabel}.`;

  const shouldIndex = articles.length > 0;

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />

        <meta
          name="robots"
          content={shouldIndex ? "index, follow" : "noindex, follow"}
        />

        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta
          property="og:site_name"
          content="Valentin-Marko – Software Development Blog"
        />
        <meta
          property="og:image"
          content="https://valentinmarko.com/about.webp"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Head>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: title,
            url: canonicalUrl,
            description,
            isPartOf: {
              "@type": "Blog",
              name: "Valentin Marko – Software Development Blog",
              url: `${siteUrl}/blog`,
            },
            about: { "@type": "Thing", name: tagLabel },
            mainEntity: {
              "@type": "ItemList",
              itemListElement: articles.map((article, index) => ({
                "@type": "ListItem",
                position: index + 1,
                url: `${siteUrl}/blog/article/${article.slug}`,
              })),
            },
          }),
        }}
      />

      <Container sx={{ minHeight: "calc(100vh - 200px)" }}>
        <Grid container spacing={3} marginTop={"34px"} marginBottom={"34px"}>
          <Grid size={{ xs: 12 }}>
            <Typography
              component={"h1"}
              variant="h1"
              sx={{
                fontSize: { xs: "25px", sm: "35px", lg: "44px" },
                fontWeight: 900,
                color: "#2D3748",
                textTransform: "capitalize",
              }}
            >
              {tagLabel}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography
              component={"p"}
              variant="h5"
              sx={{
                fontSize: { xs: "14px", sm: "18px", lg: "20px" },
                fontWeight: 600,
                color: "#718096",
                marginBottom: "30px",
              }}
            >
              {`Explore the latest articles with the tag: `}
              <strong>{tagLabel}</strong>
            </Typography>
          </Grid>
          {articles.map((article) => (
            <Grid size={{ xs: 12, md: 6 }} key={article.id}>
              <CategoryCard
                slug={article.slug}
                topic={article.topic.name}
                topicSlug={article.topic.slug}
                title={article.name}
                articleImage={article.articleImageUrl}
                description={article.shortDescription}
                author={"Valentin-Marko Cîmpan"}
                date={article.updatedAt}
                readTime={"2min"}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
    </>
  );
}

TagArticles.getLayout = (page) => <DefaultLayout>{page}</DefaultLayout>;

export default TagArticles;
